/**
 * Onboarding Demo — V16 3v3 战斗演示回放
 *
 * 职责：
 *   - 播放预录的 3v3 战斗演示（简化版：事件流 + Canvas 动画）
 *   - 演示结束页（叙事收束）
 *
 * 简化策略（v9）：
 *   - 不加载完整 headless replay 文件
 *   - 用预录事件流 + 简易 Canvas 动画模拟战斗画面
 *   - 重点在叙事冲击力，不在战斗精度
 */

import { setOnboardingStep } from "./onboarding-state";

// ===== 预录 3v3 事件流 =====

interface DemoEvent {
  time: number;  // 秒
  text: string;
  highlight?: "red" | "blue";
}

const DEMO_EVENTS: DemoEvent[] = [
  { time: 0, text: "承刃系统 · 推演记录回放" },
  { time: 0.5, text: "编号：CB-0042 · 密级：砾岩" },
  { time: 1.0, text: "红方：CH-03 + CH-05 + CH-11" },
  { time: 1.5, text: "蓝方：CH-04 + CH-06 + CH-12" },
  { time: 2.0, text: "地图：废弃炼油厂（25×25）" },
  { time: 2.5, text: "▶ 推演开始" },
  { time: 3.0, text: "> CH-03 scan 发现 CH-04", highlight: "red" },
  { time: 4.0, text: "> CH-05 向掩体移动", highlight: "red" },
  { time: 5.0, text: "> CH-11 侧翼包抄", highlight: "red" },
  { time: 6.0, text: "> CH-03 开火命中 CH-04", highlight: "red" },
  { time: 7.0, text: "> CH-04 已被淘汰", highlight: "blue" },
  { time: 8.0, text: "> CH-05 与 CH-03 会合", highlight: "red" },
  { time: 9.0, text: "> CH-06 反击命中 CH-11", highlight: "blue" },
  { time: 10.0, text: "> CH-03 开火命中 CH-06", highlight: "red" },
  { time: 11.0, text: "> CH-06 已被淘汰", highlight: "blue" },
  { time: 12.0, text: "> CH-12 孤军固守" },
  { time: 13.0, text: "> CH-03+CH-05 集火", highlight: "red" },
  { time: 14.0, text: "> CH-12 已被淘汰", highlight: "blue" },
  { time: 15.0, text: "▸ 红方胜 · 存活 2 台" },
];

/** 总时长（秒） */
export const DEMO_DURATION = 16;

// ===== API =====

/**
 * 播放 3v3 演示
 * @param container 父容器
 * @returns Promise 在播放完成或跳过时 resolve
 */
export function playDemoReplay(container: HTMLElement): Promise<void> {
  setOnboardingStep("demo");

  return new Promise((resolve) => {
    let cancelled = false;
    let eventIndex = 0;
    const timers: number[] = [];

    // 创建演示画面
    const overlay = document.createElement("div");
    overlay.className = "onboarding-demo-overlay";
    overlay.innerHTML = `
      <div class="onboarding-demo-header">
        <div class="onboarding-demo-title">推演记录回放 · CB-0042</div>
      </div>
      <div class="onboarding-demo-canvas-wrap">
        <canvas class="onboarding-demo-canvas" width="600" height="400"></canvas>
        <div class="onboarding-demo-feed"></div>
      </div>
      <div class="onboarding-demo-controls">
        <button class="onboarding-demo-skip">跳过 ▸▸</button>
      </div>
    `;
    container.appendChild(overlay);

    const feedEl = overlay.querySelector(".onboarding-demo-feed") as HTMLElement;
    const canvas = overlay.querySelector(".onboarding-demo-canvas") as HTMLCanvasElement;
    const skipBtn = overlay.querySelector(".onboarding-demo-skip") as HTMLButtonElement;
    const ctx = canvas.getContext("2d")!;

    // 简易 Canvas 动画：画几个移动的方块代表坦克
    let animFrame = 0;
    function drawCanvas() {
      if (cancelled) return;
      ctx.fillStyle = "#0a0e14";
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 网格
      ctx.strokeStyle = "#1a2332";
      ctx.lineWidth = 1;
      for (let i = 0; i <= 10; i++) {
        ctx.beginPath();
        ctx.moveTo((i * canvas.width) / 10, 0);
        ctx.lineTo((i * canvas.width) / 10, canvas.height);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(0, (i * canvas.height) / 10);
        ctx.lineTo(canvas.width, (i * canvas.height) / 10);
        ctx.stroke();
      }

      // 红方坦克（3 个）
      const t = animFrame * 0.02;
      ctx.fillStyle = "#4EC9B0";
      for (let i = 0; i < 3; i++) {
        const x = 50 + i * 80 + Math.sin(t + i) * 30;
        const y = 100 + i * 80 + Math.cos(t + i) * 20;
        ctx.fillRect(x - 8, y - 8, 16, 16);
      }

      // 蓝方坦克（逐渐消失）
      ctx.fillStyle = "#F44747";
      const aliveBlue = Math.max(0, 3 - Math.floor(animFrame / 200));
      for (let i = 0; i < aliveBlue; i++) {
        const x = canvas.width - 80 - i * 60;
        const y = 100 + i * 80;
        ctx.fillRect(x - 8, y - 8, 16, 16);
      }

      animFrame++;
      requestAnimationFrame(drawCanvas);
    }
    drawCanvas();

    // 按时间线显示事件
    function showNextEvent() {
      if (cancelled || eventIndex >= DEMO_EVENTS.length) {
        finishDemo();
        return;
      }

      const event = DEMO_EVENTS[eventIndex];
      const line = document.createElement("div");
      line.className = `onboarding-demo-feed-line ${event.highlight ? `feed-${event.highlight}` : ""}`;
      line.textContent = event.text;
      feedEl.appendChild(line);
      feedEl.scrollTop = feedEl.scrollHeight;

      eventIndex++;
      if (eventIndex < DEMO_EVENTS.length) {
        const nextTime = DEMO_EVENTS[eventIndex].time;
        const delay = (nextTime - event.time) * 1000;
        const timer = window.setTimeout(showNextEvent, delay);
        timers.push(timer);
      } else {
        const timer = window.setTimeout(finishDemo, 1500);
        timers.push(timer);
      }
    }

    function finishDemo() {
      if (cancelled) return;
      cancelled = true;
      timers.forEach((t) => clearTimeout(t));
      overlay.classList.add("onboarding-demo-fading");
      window.setTimeout(() => {
        overlay.remove();
        resolve();
      }, 500);
    }

    // 跳过
    skipBtn.addEventListener("click", () => {
      finishDemo();
    });

    // 开始播放
    const startTimer = window.setTimeout(showNextEvent, 500);
    timers.push(startTimer);
  });
}

/**
 * 渲染演示结束页
 * @param container 父容器
 * @returns Promise 在用户点击「进入铸剑阁」时 resolve
 */
export function renderDemoEnd(container: HTMLElement): Promise<void> {
  setOnboardingStep("demo-end");

  return new Promise((resolve) => {
    const overlay = document.createElement("div");
    overlay.className = "onboarding-demo-end-overlay";
    overlay.innerHTML = `
      <div class="onboarding-demo-end-content">
        <p class="onboarding-demo-end-line">这就是承刃推演官的工作。</p>
        <p class="onboarding-demo-end-line">CH-03 包抄、CH-05 会合、CH-11 牺牲</p>
        <p class="onboarding-demo-end-line">——所有决策都来自一段 Python 代码。</p>
        <div class="onboarding-demo-end-quote">
          <p>你不需要手速，不需要瞄准。</p>
          <p>你只需要写出判断。</p>
          <p>你的 Agent 会替你执行。</p>
        </div>
        <p class="onboarding-demo-end-line">你的第一场推演已经准备好了。</p>
        <button class="onboarding-demo-end-btn">进入铸剑阁 →</button>
      </div>
    `;
    container.appendChild(overlay);

    const btn = overlay.querySelector(".onboarding-demo-end-btn") as HTMLButtonElement;
    btn.addEventListener("click", () => {
      overlay.remove();
      resolve();
    });

    requestAnimationFrame(() => {
      overlay.classList.add("onboarding-demo-end-visible");
      btn.classList.add("pulse-highlight");
    });
  });
}
